import type { ReactElement } from "react";
import React, { useCallback, useEffect, useState } from "react";
import type { LoadingStates } from "@arenko-group/arenko-ui";
import {
  DataTestIdContext,
  fetchWrapper,
  loadingStates,
  useLoadingStatus,
} from "@arenko-group/arenko-ui";
import { setCustomerName, setUserInfo } from "@/features/userSlice";
import { getEnv } from "@/helpers/env.helpers";
import { getJwtToken } from "@/helpers/storage.helpers";
import { isInIframe } from "@/helpers/user.helpers";
import { topLocation } from "@/helpers/window.helpers";
import { useAppDispatch } from "@/storeHooks";
import type { TBUserType } from "@/types/data";
import { getCustomerName, handleHttpError } from "./AuthorisedUser.helpers";

export type AuthorisedUserProps = {
  component: ReactElement;
  dataTestId?: string;
};

/**
 * A wrapper that will authorizes the user,
 * stores user properties in a redux slice,
 * provides test-id context and renders component.
 *
 * Note: assumes JWT token is set before application loads.
 */
const AuthorisedUser = ({
  component,
  dataTestId = "",
}: AuthorisedUserProps) => {
  const dispatch = useAppDispatch();
  const [loadingStatus, setLoadingStatus] = useState<LoadingStates>(
    loadingStates.loading
  );

  const fetchUserData = useCallback(async () => {
    try {
      // Fetch user data
      const res: TBUserType = await fetchWrapper<TBUserType>("/api/auth/user", {
        useXAuthHeader: true,
      });

      dispatch(setUserInfo(res));

      const defaultCustomerName = "Arenko Group";
      let customerName = defaultCustomerName;

      // If the user has CUSTOMER_USER authority, fetch customer details
      if (res.authority === "CUSTOMER_USER") {
        customerName = await getCustomerName(
          res.customerId.id,
          defaultCustomerName
        );
      }
      dispatch(setCustomerName(customerName));
      setLoadingStatus(loadingStates.loaded);
    } catch (httpError: unknown) {
      setLoadingStatus(handleHttpError(httpError));
    }
  }, [dispatch]);

  useEffect(() => {
    const isInPast = getJwtToken() === false;
    if (isInPast) {
      if (isInIframe() || getEnv() !== "local") {
        topLocation().replace(`/login?redirect=true`);
        return;
      } else {
        setLoadingStatus(loadingStates.expirationError);
        return;
      }
    }
    setLoadingStatus(loadingStates.loading);
    fetchUserData();
  }, [dispatch, fetchUserData]);
  // TODO - data test id context should be taken outside this component to separate concerns
  const componentWithResponse = (
    <DataTestIdContext.Provider value={dataTestId}>
      {component}
    </DataTestIdContext.Provider>
  );

  return useLoadingStatus(loadingStatus, componentWithResponse, {
    container: true,
    topPadding: loadingStatus === loadingStates.loaded ? 50 : 10,
  });
};

export default AuthorisedUser;
